// Phase 11B — Material Symbols retirement.
//
// The product's option data (`features/settings/settingsOptions.js`,
// `features/onboarding/muscleRegionMapping.js`, `features/onboarding/
// profileSummary.js` and the cardio copy in `AdditionalContextStep`) has always
// carried a glyph key per option. Those keys used to be Google Material Symbols
// ligatures rendered by a render-blocking web font; this module draws the SAME
// keys as inline SVG on the Lyft Zone 24x24 stroke grid.
//
// This is deliberately NOT a general icon system: it holds exactly the glyphs
// the shipped option data names -- nothing more -- and it is imported only by
// the surfaces that render that data. Single-use chrome glyphs (a chevron, a
// close, a back arrow) stay inline in the component that owns them.
//
// Sizing and stroke come from `.lz-v2-glyph` in the Design V2 primitives, so
// every existing font-size-driven rule keeps controlling the rendered box.

const GLYPHS = {
  // Strength / training
  fitness_center: <path d="M3 10v4M6 8v8M18 8v8M21 10v4M6 12h12" />,
  exercise: (
    <>
      <path d="M6.2 17.8 17.8 6.2" />
      <path d="M3.4 14.6 9.4 20.6M6.2 11.8l6 6" />
      <path d="M20.6 9.4 14.6 3.4M17.8 12.2l-6-6" />
    </>
  ),
  sports_gymnastics: (
    <>
      <circle cx="15" cy="5" r="2" />
      <path d="M14.5 8 10.5 11 6 9.2" />
      <path d="M14.5 8v4.5L18 16.5" />
      <path d="M13.5 12 10 20" />
    </>
  ),
  self_improvement: (
    <>
      <circle cx="12" cy="5" r="2" />
      <path d="M12 8v4" />
      <path d="M12 12 7.5 14.8 4 13.2M12 12l4.5 2.8L20 13.2" />
      <path d="M7.5 18.5h9" />
    </>
  ),
  directions_run: (
    <>
      <circle cx="14.2" cy="4.8" r="2" />
      <path d="M13 8.2 9.6 10l-.9 4 3-1 2 3.4-1 4.4" />
      <path d="M12.6 12.4 16 14l1 3.4" />
      <path d="M9.6 10 6 11.2" />
    </>
  ),
  accessibility_new: (
    <>
      <circle cx="12" cy="4.5" r="2" />
      <path d="M4.5 8.5h15" />
      <path d="M12 8.5v4" />
      <path d="M9 20.5l3-8 3 8" />
    </>
  ),
  airline_seat_legroom_extra: (
    <>
      <circle cx="6.2" cy="4.9" r="2" />
      <path d="M6.2 7.2v4.6a2.8 2.8 0 0 0 2.8 2.8h4" />
      <path d="M13 14.6 17.4 20.6" />
      <path d="M20.6 20.6h-3.9" />
    </>
  ),
  steps: <path d="M3.5 20.5h4.5V16h4.5v-4.5H17V7h3.5" />,
  more_horiz: (
    <>
      <circle cx="5.5" cy="12" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.35" fill="currentColor" stroke="none" />
    </>
  ),

  // Person / profile
  person: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20.5a7 7 0 0 1 14 0" />
    </>
  ),
  neurology: (
    <>
      <path d="M9.4 19.8a3 3 0 0 1-3-3v-.5a2.9 2.9 0 0 1-1.3-5 3.1 3.1 0 0 1 1.7-4.4A3.3 3.3 0 0 1 12 5.2a3.3 3.3 0 0 1 5.2 1.7 3.1 3.1 0 0 1 1.7 4.4 2.9 2.9 0 0 1-1.3 5v.5a3 3 0 0 1-3 3" />
      <path d="M12 5.2v14.6" />
      <path d="M8.6 9.6h1.6M13.8 9.6h1.6M9.2 14.4h1M13 14.4h1" />
    </>
  ),
  school: (
    <>
      <path d="M12 4 2.5 8.8 12 13.6l9.5-4.8z" />
      <path d="M6.6 11.2V16c0 1.7 2.4 3.2 5.4 3.2s5.4-1.5 5.4-3.2v-4.8" />
      <path d="M21.5 8.8v5.4" />
    </>
  ),
  workspace_premium: (
    <>
      <circle cx="12" cy="8.8" r="5.3" />
      <path d="M8.6 13.4 7.2 20.8 12 18.3l4.8 2.5-1.4-7.4" />
    </>
  ),
  trending_up: (
    <>
      <path d="M3.5 16.5 10 10l3.5 3.5 7-7" />
      <path d="M15.5 6.5h5v5" />
    </>
  ),
  favorite: (
    <path d="M12 20.2s-7.5-4.6-7.5-9.5A4.1 4.1 0 0 1 12 8.1a4.1 4.1 0 0 1 7.5 2.6c0 4.9-7.5 9.5-7.5 9.5z" />
  ),
  monitor_heart: (
    <>
      <rect x="2.8" y="5" width="18.4" height="14" rx="2.6" />
      <path d="M6 12.4h2.4l1.6-3.2 2.6 6.4 1.5-3.2H18" />
    </>
  ),

  // Time / scheduling
  timer: (
    <>
      <circle cx="12" cy="13.6" r="7" />
      <path d="M12 10.2v3.4h2.6" />
      <path d="M9.5 3.5h5" />
    </>
  ),
  calendar_month: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3.2v3.6M16 3.2v3.6" />
      <path d="M7.8 14h.01M12 14h.01M16.2 14h.01M7.8 17.4h.01M12 17.4h.01" />
    </>
  ),

  // Environment / equipment
  home: (
    <>
      <path d="M3.8 10.6 12 4l8.2 6.6" />
      <path d="M6 9.8V20.2h12V9.8" />
      <path d="M10 20.2v-5h4v5" />
    </>
  ),
  apartment: (
    <>
      <path d="M4.5 20.5V4h9v16.5" />
      <path d="M13.5 10h6v10.5" />
      <path d="M7.5 7.5h3M7.5 11.5h3M7.5 15.5h3M16 13.5h.01M16 17h.01" />
    </>
  ),
  domain: (
    <>
      <path d="M3 20.5V8.5h8" />
      <path d="M11 20.5V3.5h10v17" />
      <path d="M5.8 12h2.4M5.8 16h2.4M14.5 7.5h3M14.5 12h3M14.5 16.5h3" />
    </>
  ),
  garage_home: (
    <>
      <path d="M3.5 20.5V9.4L12 3.8l8.5 5.6v11.1" />
      <path d="M7 20.5v-6.4h10v6.4" />
      <path d="M7 17.3h10" />
    </>
  ),
  deployed_code: (
    <>
      <path d="M12 3.2 20 7.6v8.8L12 20.8 4 16.4V7.6z" />
      <path d="M4 7.6 12 12l8-4.4M12 12v8.8" />
    </>
  ),
  precision_manufacturing: (
    <>
      <path d="M3.4 20.6h10.2" />
      <path d="M6.4 20.6v-4.4l2.6-4.6" />
      <circle cx="10" cy="9.6" r="2.1" />
      <path d="M12 8.8 15.6 7.4" />
      <path d="M15.2 4.4h5.4v5.4h-5.4z" />
      <path d="M17.9 9.8v2.4" />
    </>
  ),
  build: (
    <path d="M15.4 3.6a5 5 0 0 0-5.9 6.5l-5.8 5.8a2 2 0 0 0 2.8 2.8l5.8-5.8a5 5 0 0 0 6.5-5.9l-3 3-2.4-2.4z" />
  ),
  bolt: <path d="M13.6 3 6.4 13.4h4.9L10 21l7.6-10.6h-4.9z" />,
  tune: (
    <>
      <path d="M3.5 8h9.5M18.5 8h2M3.5 16h3.5M12.5 16h8" />
      <circle cx="15.7" cy="8" r="2.2" />
      <circle cx="9.7" cy="16" r="2.2" />
    </>
  ),
  shuffle: (
    <>
      <path d="M3.5 6.8h3.3l9 10.4h4.2" />
      <path d="M3.5 17.2h3.3l2.7-3.2M13.4 9.9l2.4-3.1h4.2" />
      <path d="M17.8 3.8 20.8 6.8l-3 3M17.8 14.2l3 3-3 3" />
    </>
  ),

  // State
  check_circle: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M8.3 12.2 11 14.9l4.7-5.4" />
    </>
  ),
  visibility: (
    <>
      <path d="M2.6 12S6.1 6.4 12 6.4 21.4 12 21.4 12 17.9 17.6 12 17.6 2.6 12 2.6 12z" />
      <circle cx="12" cy="12" r="2.7" />
    </>
  ),
  shield: (
    <path d="M12 3.4 19.4 6v6c0 4.2-3 7.1-7.4 8.6C7.6 19.1 4.6 16.2 4.6 12V6z" />
  ),
  block: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M6.1 6.1 17.9 17.9" />
    </>
  ),
  do_not_disturb: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M7.8 12h8.4" />
    </>
  ),
  do_not_disturb_on: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M7.8 12h8.4" />
    </>
  ),
  all_inclusive: (
    <path d="M8.2 8.4a4.5 4.5 0 1 0 0 7.2c2.2 0 3.4-1.8 4.8-3.6s2.6-3.6 4.8-3.6a4.5 4.5 0 1 1 0 7.2c-2.2 0-3.4-1.8-4.8-3.6S10.4 8.4 8.2 8.4z" />
  ),
};

// Renders the glyph an option's own `icon` key names. Decorative by contract --
// every call site keeps the accessible name it already had on the surrounding
// control, so an unknown key renders nothing rather than a fallback shape.
export default function OptionGlyph({ name, className = "" }) {
  const glyph = GLYPHS[name];

  if (!glyph) {
    return null;
  }

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={["lz-v2-glyph", className].filter(Boolean).join(" ")}
    >
      {glyph}
    </svg>
  );
}

export const OPTION_GLYPH_NAMES = Object.keys(GLYPHS);
